import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Clock,
    MapPin,
    Phone,
    Calendar,
    ChevronRight,
    MessageCircle,
    ShieldCheck,
    Star,
    Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const PublicBookingPage = () => {
    const { slug } = useParams();
    const [loading, setLoading] = useState(true);
    const [clinic, setClinic] = useState(null);
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchClinicData = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/booking/public/${slug}`);
                if (response.ok) {
                    const data = await response.json();
                    setClinic(data.page);
                    setServices(data.services);
                } else {
                    setError("Clínica no encontrada");
                }
            } catch (err) {
                console.error("Error fetching clinic data:", err);
                setError("Error de conexión");
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchClinicData();
        }
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (error || !clinic) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8fafc] p-4 text-center">
                <h1 className="text-4xl font-bold text-slate-900 mb-2">404</h1>
                <p className="text-xl text-slate-500 mb-8">{error || "Lo sentimos, esta página ya no existe."}</p>
                <Button onClick={() => window.location.href = "/"} className="bg-blue-600">
                    Volver al inicio
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f8fafc]">
            {/* Header / Banner */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    <div className="flex flex-col md:flex-row items-center gap-6">
                        <div
                            className="w-24 h-24 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200 overflow-hidden"
                            style={{ backgroundColor: clinic.theme_color || '#2563eb' }}
                        >
                            {clinic.logo_url ? (
                                <img src={clinic.logo_url} alt={clinic.clinic_name} className="w-full h-full object-cover" />
                            ) : (
                                <ShieldCheck className="h-12 w-12" />
                            )}
                        </div>
                        <div className="text-center md:text-left">
                            <h1 className="text-3xl font-bold text-slate-900">{clinic.clinic_name}</h1>
                            <p className="text-slate-500 mt-1 max-w-lg">{clinic.description}</p>
                            <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-4 text-sm text-slate-400">
                                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {clinic.address || 'Ubicación no especificada'}</span>
                                <span className="flex items-center gap-1"><Star className="h-4 w-4 text-amber-400" /> 5.0 (Reseñas recientes)</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-4xl mx-auto px-4 py-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Services List */}
                    <div className="md:col-span-2 space-y-6">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="text-xl font-bold text-slate-800">Selecciona un servicio</h2>
                            <span className="text-sm font-medium text-blue-600" style={{ color: clinic.theme_color }}>
                                {services.length} disponibles
                            </span>
                        </div>

                        {services.length === 0 ? (
                            <div className="p-12 text-center bg-white rounded-2xl border border-dashed border-slate-200 text-slate-400">
                                No hay servicios disponibles en este momento.
                            </div>
                        ) : (
                            services.map((service) => (
                                <Card
                                    key={service.id}
                                    onClick={() => setSelectedService(service)}
                                    className={`cursor-pointer transition-all border-2 ${selectedService?.id === service.id ? 'border-blue-500 bg-blue-50/30' : 'border-white hover:border-slate-200'}`}
                                    style={{ borderColor: selectedService?.id === service.id ? clinic.theme_color : 'transparent' }}
                                >
                                    <CardContent className="p-0">
                                        <div className="flex flex-col sm:flex-row">
                                            <div className="sm:w-32 h-32 sm:h-auto overflow-hidden bg-slate-100 flex items-center justify-center text-slate-300">
                                                {service.image_url ? (
                                                    <img src={service.image_url} alt={service.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <Calendar className="h-8 w-8" />
                                                )}
                                            </div>
                                            <div className="flex-1 p-5 flex flex-col justify-between">
                                                <div>
                                                    <div className="flex justify-between items-start mb-1">
                                                        <h3 className="font-bold text-slate-900">{service.name}</h3>
                                                        <span className="font-bold text-blue-600 text-lg" style={{ color: clinic.theme_color }}>
                                                            ${service.price}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-slate-500 line-clamp-2">{service.description}</p>
                                                </div>
                                                <div className="flex items-center gap-4 mt-4">
                                                    <span className="flex items-center gap-1.5 text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded-full">
                                                        <Clock className="h-3 w-3" /> {service.duration}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        className="ml-auto hover:bg-blue-50 p-2 h-auto text-xs font-bold gap-1 uppercase tracking-wider"
                                                        style={{ color: clinic.theme_color }}
                                                    >
                                                        Elegir <ChevronRight className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>

                    {/* Sidebar / Info */}
                    <div className="space-y-6">
                        <Card className="bg-white border-slate-200 sticky top-8">
                            <CardContent className="p-6">
                                <h3 className="font-bold text-slate-900 mb-4">Información de contacto</h3>
                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                                            <Phone className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-400 uppercase">Teléfono</p>
                                            <p className="text-sm font-medium text-slate-700">{clinic.contact_phone || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                                            <Calendar className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-400 uppercase">Dirección</p>
                                            <p className="text-sm font-medium text-slate-700">{clinic.address || 'Consulta en línea'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <p className="text-xs text-slate-400 mb-4 text-center">Impulsado por <b>ChatAlfa</b></p>
                                    <Button
                                        onClick={() => window.open(`https://wa.me/${clinic.contact_phone?.replace(/\D/g, '')}`, '_blank')}
                                        className="w-full bg-[#25D366] hover:bg-[#128C7E] flex items-center justify-center gap-2 text-white font-bold border-none"
                                    >
                                        <MessageCircle className="h-5 w-5" />
                                        Chatear con nosotros
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>

            {/* Floating Action Mobile */}
            {selectedService && (
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 md:hidden z-50 animate-in slide-in-from-bottom duration-300">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                            <p className="text-xs text-slate-400 uppercase font-bold">Seleccionado</p>
                            <p className="font-bold text-slate-900 truncate">{selectedService.name}</p>
                        </div>
                        <Button
                            className="bg-blue-600 hover:bg-blue-700 font-bold px-8 shadow-lg shadow-blue-200"
                            style={{ backgroundColor: clinic.theme_color }}
                        >
                            Continuar
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicBookingPage;
